namespace $.$$ {

	type $shm_opencode_queue_item = {
		id: string
		text: string
		attachments: $shm_agent_attachment[]
		createdAt: number
	}

	export class $shm_opencode_app extends $.$shm_opencode_app {
		agent_app(): any { return this }

		@ $mol_mem
		opencode_providers_data() {
			this.opencode_providers_refresh()
			return $mol_wire_sync( $shm_opencode_api ).providers( this.server_url() ) as {
				providers: $shm_opencode_api_provider[]
				default: Record< string, string >
			}
		}

		@ $mol_mem
		opencode_providers_refresh( next?: number ) { return next ?? 0 }

		opencode_providers() { return this.opencode_providers_data().providers ?? [] }

		@ $mol_mem
		opencode_config_data() {
			return $mol_wire_sync( $shm_opencode_api ).config_resolved( this.server_url() ) as $shm_opencode_api_config
		}

		opencode_config_model() {
			const model = this.opencode_config_data().model
			if( typeof model !== 'string' ) return null
			const divider = model.indexOf( '/' )
			if( divider === -1 ) return null
			return { providerID: model.slice( 0, divider ), modelID: model.slice( divider + 1 ) }
		}

		opencode_provider_key( id: string ) { return `shm_opencode_provider:${ id }` }
		opencode_model_key( id: string ) { return `shm_opencode_model:${ id }` }

		opencode_provider_id( id: string, next?: string ) {
			if( next !== undefined ) {
				this.$.$mol_state_local.value( this.opencode_provider_key( id ), next )
				this.$.$mol_state_local.value( this.opencode_model_key( id ), '' )
				return next
			}
			const options = this.agent_provider_options()
			const stored = this.$.$mol_state_local.value( this.opencode_provider_key( id ) ) as string | null
			if( stored && options.includes( stored ) ) return stored
			const source = this.session_by_id( id )?.opencode?.model?.providerID
			if( source && options.includes( source ) ) return source
			const configured = this.opencode_config_model()?.providerID
			if( configured && options.includes( configured ) ) return configured
			return options[ 0 ] || ''
		}

		agent_provider_options() { return this.opencode_providers().map( provider => provider.id ) }

		agent_provider_dict() {
			const dict = {} as Record< string, string >
			for( const provider of this.opencode_providers() ) dict[ provider.id ] = provider.name || provider.id
			return dict
		}

		agent_provider_id( next?: string ) {
			const id = this.session_id()
			return id ? this.opencode_provider_id( id, next ) : ''
		}

		opencode_provider_current( id = this.session_id() ) {
			const selected = this.opencode_provider_id( id )
			return this.opencode_providers().find( provider => provider.id === selected ) ?? this.opencode_providers()[ 0 ]
		}

		agent_model_options() { return Object.keys( this.opencode_provider_current()?.models ?? {} ) }

		agent_model_dict() {
			const dict = {} as Record< string, string >
			const models = this.opencode_provider_current()?.models ?? {}
			for( const id in models ) dict[ id ] = models[ id ].name || id
			return dict
		}

		opencode_model_id( id: string, next?: string ) {
			if( next !== undefined ) {
				this.$.$mol_state_local.value( this.opencode_model_key( id ), next )
				return next
			}
			const options = Object.keys( this.opencode_provider_current( id )?.models ?? {} )
			const stored = this.$.$mol_state_local.value( this.opencode_model_key( id ) ) as string | null
			if( stored && options.includes( stored ) ) return stored
			const source = this.session_by_id( id )?.opencode?.model
			const source_id = source?.modelID || source?.id
			if( source?.providerID === this.opencode_provider_id( id ) && source_id && options.includes( source_id ) ) return source_id
			const configured = this.opencode_config_model()
			const configured_id = configured?.modelID
			if( configured?.providerID === this.opencode_provider_id( id ) && configured_id && options.includes( configured_id ) ) return configured_id
			return this.opencode_providers_data().default?.[ this.opencode_provider_id( id ) ] || options[ 0 ] || ''
		}

		override agent_model_id( next?: string ) {
			const id = this.session_id()
			return id ? this.opencode_model_id( id, next ) : ''
		}

		agent_settings_before_model() { return [ this.Provider_label() ] }
		agent_settings_after_model() { return [ this.Custom_provider() ] }

		@ $mol_action
		override custom_provider_add( next?: Event ) {
			if( next === undefined ) return null
			const provider_id = this.custom_provider_id().trim()
			const base_url = this.custom_provider_base_url().trim()
			const model_id = this.custom_provider_model_id().trim()
			if( !provider_id ) throw new Error( 'Provider ID is required' )
			if( !base_url ) throw new Error( 'Base URL is required' )
			if( !model_id ) throw new Error( 'Model ID is required' )
			$mol_wire_async( this ).custom_provider_add_later({
				provider_id,
				provider_name: this.custom_provider_name().trim() || provider_id,
				base_url,
				api_key: this.custom_provider_api_key().trim(),
				model_id,
				model_name: this.custom_provider_model_name().trim() || model_id,
			}).catch( $mol_fail_log )
			return next
		}

		custom_provider_add_later( data: {
			provider_id: string
			provider_name: string
			base_url: string
			api_key: string
			model_id: string
			model_name: string
		} ) {
			const config = $mol_wire_sync( $shm_opencode_api ).config( this.server_url() )
			const provider = config.provider?.[ data.provider_id ] ?? {}
			$mol_wire_sync( $shm_opencode_api ).config_update( this.server_url(), {
				... config,
				provider: {
					... config.provider,
					[ data.provider_id ]: {
						... provider,
						npm: provider.npm ?? '@ai-sdk/openai-compatible',
						name: data.provider_name,
						options: {
							... provider.options,
							baseURL: data.base_url,
							...( data.api_key ? { apiKey: data.api_key } : {} ),
						},
						models: {
							... provider.models,
							[ data.model_id ]: {
								... ( provider.models?.[ data.model_id ] ?? {} ),
								name: data.model_name,
							},
						},
					},
				},
			} )
			this.opencode_providers_refresh( Date.now() )
			this.agent_provider_id( data.provider_id )
			this.agent_model_id( data.model_id )
			this.custom_provider_expanded( false )
		}

		agent_storage_namespace() { return 'opencode' }
		agent_name() { return 'OpenCode' }

		sessions_title() { return 'OpenCode' }

		server_url( next?: string ) {
			if( next !== undefined ) return next
			const explicit = new URL( this.$.$mol_dom_context.location.href ).searchParams.get( 'server' )
			if( explicit ) return explicit
			return 'http://127.0.0.1:4096'
		}

		opencode_agent_id() { return '' }

		opencode_seconds( stamp?: number ) {
			if( !stamp ) return null
			return stamp > 10_000_000_000 ? stamp / 1000 : stamp
		}

		opencode_tags_key( id: string ) { return `shm_opencode_tags:${ id }` }

		@ $mol_mem_key
		opencode_tags( id: string, next?: readonly string[] ) {
			return ( this.$.$mol_state_local.value( this.opencode_tags_key( id ), next ) as readonly string[] | null ) ?? []
		}

		opencode_session( session: $shm_opencode_api_session, status?: { type?: string } ): $shm_agent_session {
			const raw = session as any
			const model = raw.model
			return {
				id: session.id,
				name: session.title || session.slug || `Сессия ${ session.id.slice( 0, 8 ) }`,
				preview: session.title || '',
				cwd: session.directory || '',
				modelProvider: [ model?.providerID, model?.modelID || model?.id ].filter( Boolean ).join( '/' ) || 'OpenCode',
				createdAt: this.opencode_seconds( session.time?.created ),
				updatedAt: this.opencode_seconds( session.time?.updated ),
				status: { type: status?.type === 'busy' || status?.type === 'retry' ? 'active' : 'idle' },
				source: 'opencode',
				managedBy: 'shm_agent',
				forkedFromId: raw.parentID ?? null,
				tags: [ ... this.opencode_tags( session.id ) ],
				opencode: raw,
			}
		}

		agent_sessions_load() {
			const sessions = $mol_wire_sync( $shm_opencode_api ).sessions( this.server_url() ) as $shm_opencode_api_session[]
			const statuses = $mol_wire_sync( $shm_opencode_api ).session_statuses( this.server_url() )
			return {
				data: sessions
					.map( session => this.opencode_session( session, statuses[ session.id ] ) )
					.sort( ( left, right )=> Number( right.updatedAt ) - Number( left.updatedAt ) ),
			}
		}

		opencode_part_text( part: any ) {
			if( part?.type === 'text' ) return String( part.text || '' )
			if( part?.type === 'reasoning' ) return ''
			return ''
		}

		opencode_part_value( part: any ) {
			if( part?.type === 'reasoning' ) return String( part.text || '' ).trim()
			if( part?.type !== 'tool' ) return ''
			const state = part.state ?? {}
			const input = state.input ?? part.input
			const output = state.output ?? state.result ?? part.output
			const stringify = ( value: any )=> {
				if( value == null || value === '' ) return ''
				if( typeof value === 'string' ) return value
				try { return JSON.stringify( value, null, 2 ) } catch { return String( value ) }
			}
			return [
				state.status ? `Статус: ${ state.status }` : '',
				input != null ? `Вход:\n\n\`\`\`json\n${ stringify( input ) }\n\`\`\`` : '',
				output != null ? `Результат:\n\n${ stringify( output ) }` : '',
			].filter( Boolean ).join( '\n\n' ).slice( 0, 12_000 )
		}

		opencode_reasoning( message: $shm_opencode_api_message ) {
			let reasoning_index = 0
			return message.parts.flatMap( ( part: any )=> {
				if( ![ 'reasoning', 'tool' ].includes( part?.type ) ) return []
				const text = this.opencode_part_value( part )
				if( !text ) return []
				if( part.type === 'reasoning' ) ++ reasoning_index
				return [ {
					type: part.type,
					title: part.type === 'tool' ? `tool · ${ part.name || 'инструмент' }` : `Размышление ${ reasoning_index }`,
					text,
				} ]
			} )
		}

		opencode_message_item( message: $shm_opencode_api_message ) {
			const role = message.info.role || message.info.type || ''
			const error = String( message.info.error?.data?.message || message.info.error?.message || '' )
			const text = [
				message.parts.map( part => this.opencode_part_text( part ) ).filter( Boolean ).join( '\n\n' ),
				...( error ? [ `Ошибка: ${ error }` ] : [] ),
			].filter( Boolean ).join( '\n\n' )
			const images = message.parts
				.filter( part => part?.type === 'file' && String( part?.mime || '' ).startsWith( 'image/' ) )
				.map( part => String( part.url || '' ) ).filter( Boolean )
			const started = this.opencode_seconds( message.info.time?.created ) || Date.now() / 1000
			const reasoning = this.opencode_reasoning( message )
			if( role === 'user' ) return {
				id: message.info.id,
				sourceId: message.info.id,
				type: 'userMessage',
				content: [ ...( text ? [ { type: 'text', text } ] : [] ), ... images.map( url => ({ type: 'image', url }) ) ],
				images,
				turnStartedAt: started,
			}
			if( role === 'assistant' && ( text || images.length || reasoning.length ) ) return {
				id: message.info.id,
				sourceId: message.info.id,
				type: 'agentMessage',
				text,
				images,
				reasoning,
				turnStartedAt: started,
				model: message.info.modelID || message.info.model?.modelID || message.info.model?.id,
			}
			return null
		}

		opencode_message_visible( message: $shm_opencode_api_message ) {
			return true
		}

		opencode_thread_data(
			session: $shm_opencode_api_session,
			messages: $shm_opencode_api_message[],
			status?: { type?: string },
		): $shm_agent_thread_data {
			const normalized = this.opencode_session( session, status )
			const turns = messages.filter( message => this.opencode_message_visible( message ) ).flatMap( message => {
				const item = this.opencode_message_item( message )
				return item ? [ { id: message.info.id, status: 'completed', startedAt: item.turnStartedAt, items: [ item ] } ] : []
			} )
			const busy = status?.type === 'busy' || status?.type === 'retry'
			return {
				thread: { ... normalized, turns },
				activeTurnId: busy ? `opencode:${ session.id }` : null,
				queue: [ ... this.opencode_queue( session.id ) ],
			}
		}

		agent_thread_load( id: string, refresh: number ) {
			const session = $mol_wire_sync( $shm_opencode_api ).session( this.server_url(), id, refresh )
			const messages = $mol_wire_sync( $shm_opencode_api ).messages( this.server_url(), id, refresh ) as $shm_opencode_api_message[]
			const statuses = $mol_wire_sync( $shm_opencode_api ).session_statuses( this.server_url(), refresh )
			return this.opencode_thread_data( session, messages, statuses[ id ] )
		}

		agent_session_create( title: string, cwd: string, instructions: string ) {
			const session = $mol_wire_sync( $shm_opencode_api ).create_session( this.server_url(), title, null ) as $shm_opencode_api_session
			if( instructions ) $mol_wire_sync( $shm_opencode_api ).send_prompt(
				this.server_url(), session.id, instructions, null, true, this.opencode_agent_id(),
			)
			return { thread: this.opencode_session( session ) }
		}

		async agent_session_create_async( title: string, cwd: string, instructions: string ) {
			const session = await $shm_opencode_api.create_session( this.server_url(), title, null ) as $shm_opencode_api_session
			if( instructions ) await $shm_opencode_api.send_prompt(
				this.server_url(), session.id, instructions, null, true, this.opencode_agent_id(),
			)
			return { thread: this.opencode_session( session ) }
		}

		agent_session_rename( id: string, name: string ) {
			const session = $mol_wire_sync( $shm_opencode_api ).session_update( this.server_url(), id, { title: name } )
			return { thread: this.opencode_session( session ) }
		}

		async agent_session_tags( id: string, tags: readonly string[] ) {
			this.opencode_tags( id, [ ... tags ] )
			const session = await $shm_opencode_api.session( this.server_url(), id )
			return { thread: { ... this.opencode_session( session ), tags: [ ... tags ] } }
		}

		opencode_thread_refresh() {
			const app = this as any
			app.thread_refresh( app.thread_refresh() + 1 )
		}

		agent_session_remove( id: string ) {
			this.opencode_queue( id, [] )
			this.opencode_tags( id, [] )
			return $shm_opencode_api.session_delete( this.server_url(), id )
		}

		agent_session_fork( id: string ) {
			const session = $mol_wire_sync( $shm_opencode_api ).session_fork( this.server_url(), id )
			return { thread: this.opencode_session( session ) }
		}

		opencode_model( id: string ) {
			const providerID = this.opencode_provider_id( id )
			const modelID = this.opencode_model_id( id )
			return providerID && modelID ? { providerID, modelID } : null
		}

		opencode_before_send( id: string ) {}

		opencode_send_now( id: string, text: string, attachments: readonly $shm_agent_attachment[] ) {
			this.opencode_before_send( id )
			const parts = [
				...( text ? [ { type: 'text' as const, text } ] : [] ),
				... attachments.map( attachment => ({
					type: 'file' as const,
					mime: attachment.type || 'application/octet-stream',
					filename: attachment.name,
					url: attachment.data,
				}) ),
			]
			return $mol_wire_sync( $shm_opencode_api ).send_parts(
				this.server_url(), id, parts, this.opencode_model( id ), this.opencode_agent_id(),
			)
		}

		opencode_queue_key( id: string ) { return `shm_opencode_queue:${ id }` }

		@ $mol_mem_key
		opencode_queue( id: string, next?: readonly $shm_opencode_queue_item[] ) {
			return ( this.$.$mol_state_local.value( this.opencode_queue_key( id ), next ) as readonly $shm_opencode_queue_item[] | null ) ?? []
		}

		agent_message_send(
			id: string,
			text: string,
			attachments: readonly $shm_agent_attachment[],
			mode: 'start' | 'queue' | 'steer',
			client_message_id: string,
		) {
			if( mode === 'queue' ) {
				const item: $shm_opencode_queue_item = {
					id: `opencode:${ client_message_id || $mol_guid() }`,
					text,
					attachments: attachments.map( item => ({ ... item, file: undefined }) ),
					createdAt: Date.now() / 1000,
				}
				this.opencode_queue( id, [ ... this.opencode_queue( id ), item ] )
				return { queued: true, message: item }
			}
			this.opencode_send_now( id, text, attachments )
			return mode === 'steer' ? { steered: true } : { started: true }
		}

		async agent_message_force( thread: string, queue: string ) {
			const item = this.opencode_queue( thread ).find( item => item.id === queue )
			if( !item ) throw new Error( 'Сообщение уже вышло из очереди' )
			this.opencode_queue( thread, this.opencode_queue( thread ).filter( item => item.id !== queue ) )
			try {
				this.opencode_before_send( thread )
				await $shm_opencode_api.send_parts_async(
					this.server_url(), thread,
					[
						...( item.text ? [ { type: 'text' as const, text: item.text } ] : [] ),
						... item.attachments.map( attachment => ({ type: 'file' as const, mime: attachment.type, filename: attachment.name, url: attachment.data }) ),
					],
					this.opencode_model( thread ),
					this.opencode_agent_id(),
				)
			} catch( error ) {
				this.opencode_queue( thread, [ item, ... this.opencode_queue( thread ) ] )
				throw error
			}
		}

		agent_session_stop( id: string ) {
			return $mol_wire_sync( $shm_opencode_api ).abort_session( this.server_url(), id )
		}

		opencode_queue_live_id( thread: string, queue: string ) {
			const live = this.agent_app().live_items( thread )
			for( const [ id, item ] of Object.entries( live ) ) {
				if( ( item as any ).queueId === queue ) return id
			}
			return ''
		}

		opencode_queue_mark_sent( thread: string, queue: string ) {
			const id = this.opencode_queue_live_id( thread, queue )
			if( id ) this.agent_app().optimistic_message_state( thread, id, '', { queued: false, queueId: '', deliveryMode: 'start' } )
			return id
		}

		async opencode_queue_drain( thread: string ) {
			const item = this.opencode_queue( thread )[ 0 ]
			if( !item ) return
			this.opencode_queue( thread, this.opencode_queue( thread ).slice( 1 ) )
			const optimistic = this.opencode_queue_mark_sent( thread, item.id )
			this.agent_app().session_busy_local( thread, true )
			try {
				this.opencode_before_send( thread )
				await $shm_opencode_api.send_parts(
					this.server_url(), thread,
					[
						...( item.text ? [ { type: 'text' as const, text: item.text } ] : [] ),
						... item.attachments.map( attachment => ({ type: 'file' as const, mime: attachment.type, filename: attachment.name, url: attachment.data }) ),
					],
					this.opencode_model( thread ),
					this.opencode_agent_id(),
				)
			} catch( error ) {
				this.opencode_queue( thread, [ item, ... this.opencode_queue( thread ) ] )
				if( optimistic ) this.agent_app().optimistic_message_state( thread, optimistic, 'В очереди', {
					queued: true,
					queueId: item.id,
					deliveryMode: 'queue',
				})
				this.agent_app().session_busy_local( thread, false )
				throw error
			}
		}

		@ $mol_mem
		events() {
			return this.$.$shm_opencode_api_events.make({
				url: ()=> this.server_url(),
				notify: $mol_const( event => this.opencode_event( event ) ),
			})
		}

		@ $mol_action
		opencode_event( raw: $shm_opencode_api_event ) {
			const event = raw.payload ?? raw
			const type = event.type
			const props = event.properties ?? {}
			const thread = String( props.sessionID || props.info?.sessionID || props.info?.id || '' )

			if( type === 'server.connected' ) {
				this.agent_app().connection_online( true )
				this.opencode_thread_refresh()
				return
			}
			if( type === 'session.created' || type === 'session.updated' ) {
				if( props.info?.id ) this.agent_app().session_upsert( this.opencode_session( props.info ) )
				return
			}
			if( type === 'session.deleted' ) {
				if( props.sessionID ) this.agent_app().session_remove( props.sessionID )
				return
			}

			if( type === 'session.status' ) {
				const status = props.status?.type || props.status || props.type
				if( thread ) this.agent_app().session_busy_local( thread, status === 'busy' || status === 'retry' )
			}
			if( type === 'session.idle' || type === 'session.error' ) {
				if( thread ) this.agent_app().session_busy_local( thread, false )
				if( type === 'session.error' && thread === this.session_id() ) this.agent_app().activity_error( String( props.error?.message || props.message || 'Ошибка OpenCode' ) )
				if( type === 'session.idle' && thread ) void this.opencode_queue_drain( thread ).catch( $mol_fail_log )
			}
			if( thread && thread !== this.session_id() && type === 'message.updated' && props.info?.role === 'assistant' ) {
				this.agent_app().session_unread_live( thread, true )
			}
			if( !thread || thread === this.session_id() ) {
				if( type.startsWith( 'message.' ) || type.startsWith( 'session.' ) ) this.opencode_thread_refresh()
			}
		}

	}

}
